
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { SMCases, SMQuestionnaire, SMUser } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { API_TCase } from "@/models/cases";
import { TO_DO_TASK_STATUS } from "@/constants/tasks";
import { API_TCaseTab } from "@/models/case_tabs";
import {
  APPROVED_BY_EXPERT_OA_STATUS,
  APPROVED_BY_HO_OA_STATUS,
  CANDIDATE_ASSESSMENT_SERVICE_TYPE,
  CANDIDATE_DETAILS_SERVICE_TAB,
  COMPANY_DETAILS_SERVICE_TAB,
  COS_SKILLED_WORKER_INSIDE_SERVICE,
  DEPENDANT_INSIDE_SERVICE_TYPE,
  EMPLOYMENT_SERVICE_TAB,
  EXPERT_REVIEW_SUBMISSION_SERVICE_STEP,
  ON_REVIEW_OA_STATUS,
  PERSONAL_DETAILS_SERVICE_TAB,
  REJECTED_BY_HO_OA_STATUS,
  SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
  SKILLED_WORKER_COS_SERVICE_TYPE,
  SKILLED_WORKER_INSIDE_SERVICE_TYPE,
  SKILLED_WORKER_SERVICE_TYPE,
  STEPS_FOR_EDITING,
  VISA_DECISION_SERVICE_STEP,
  PAYMENT_SERVICE_STEP,
  APPLICATION_SUBMISSION_SERVICE_STEP,
  DATA_AND_DOCUMENTS_SUBMISSION_SERVICE_STEP,
  ADDITIONAL_INFO_FOR_EXPERT_SERVICE_STEP,
  ADDITIONAL_DATA_AND_DOCUMENTS_SERVICE_STEP,
  IN_PROGRESS_OA_STATUS,
  READY_TO_SUBMIT_OA_STATUS,
} from "@/constants/services";

Component.registerHooks(["beforeRouteEnter", "beforeRouteUpdate", "beforeRouteLeave"]);

const SPONSORSHIP_CANDIDATE_PROFILE = "SponsorshipCandidateProfile";

const CANDIDATE_DASHBOARD_ROUTE = "candidateDashboard";
const EMPLOYER_DASHBOARD_ROUTE = "employerDashboard";
const EXPERT_DASHBOARD_ROUTE = "expertDashboard";

const QUESTIONNAIRE_STEP_TYPE_ADD = "add";

@Component({
  components: {
    Loader: () => import("@/components/Loader/Loader.vue"),
    NotificationDataNotLoaded: () =>
      import("@/components/Notifications/NotificationDataNotLoaded.vue"),
    ExpertTemplateProfile: () =>
      import("@/modules/Services/Template/views/Profile/TemplateProfile/ExpertTemplateProfile.vue"),
    CandidateOrEmployerTemplateProfile: () =>
      import(
        "@/modules/Services/Template/views/Profile/TemplateProfile/CandidateOrEmployerTemplateProfile.vue"
      ),
  },
})
export default class TemplateProfile extends Vue {
  @Prop({ type: String, default: "" }) readonly type!: string;

  init = false;

  get isExpert() {
    return SMUser.isExpert;
  }

  get isCandidate() {
    return SMUser.isCandidate;
  }

  get isEmployer() {
    return SMUser.isEmployer;
  }

  get isEmployee() {
    return SMUser.isEmployee;
  }

  service = "";

  get serviceId(): number {
    return Number(this.$route.params.id);
  }

  get sponsorshipCandidateId() {
    return Number(this.$route.params.sponsorship_candidate_id);
  }

  get profileId(): number {
    return Number(this.$route.params.tabTypeId);
  }

  // TODO: Check if relevant for logic, and we realy need it;
  @Watch("profileId")
  profileIdChanged() {
    this.fetchServiceProfile();
  }

  loading = false;

  async fetchServiceProfile() {
    if (this.loading) return;

    try {
      this.loading = true;

      // TODO: DEV-1752. Update api endpoint namespace;
      let url = "";

      if (this.type === SPONSORSHIP_CANDIDATE_PROFILE) {
        url = API_ROUTES.COMPANY_SPONSORSHIP_CANDIDATE(this.serviceId, this.sponsorshipCandidateId);
      } else {
        url = API_ROUTES.CASE_PROFILE_ID(this.serviceId, this.profileId);
      }

      const { data } = await this.axios.get(url);

      if (!data) return;

      this.service = data;
    } finally {
      this.loading = false;
    }
  }

  /**
   * NOTE: Very strange behavior of "Router" hooks. Need to discover it deeper. Ä
   * "Activated" hook work better in this case. Like "BeforeRouteUpdate"
   */
  // async beforeRouteEnter(to: any, from: any, next: NavigationGuardNext) {
  //   next(async (vm: any) => {
  //     vm.loading = false;
  //
  //     await vm.fetchServiceProfile();
  //   });
  // }

  async activated() {
    this.loading = false;

    await this.fetchServiceProfile();

    if (!SMCases.caseId) return;

    await SMCases.fetchCaseTabs(SMCases.caseId);
  }

  get dataIsEdited() {
    return SMQuestionnaire.lastUpdatedStep;
  }

  @Watch("dataIsEdited")
  dataIsEditedChanged() {
    this.fetchServiceProfile();
  }

  updateProfile() {
    this.fetchServiceProfile();
  }

  setupUpdateProfile() {
    this.$root.$on("update-profile", () => {
      this.updateProfile();
    });
  }

  mounted() {
    this.setupUpdateProfile();
  }

  // Check for tasks and edit enable;
  get tasks() {
    return (SMCases.case as API_TCase).tasks;
  }

  get toDoTasks() {
    return this.tasks?.filter((task) => task.status === TO_DO_TASK_STATUS);
  }

  get toDoTaskNotForCurrentUser() {
    return this.toDoTasks?.filter(
      (task) => task.user_id !== SMUser.user?.ID && task.user.role !== SMUser.user?.role
    );
  }

  get toDoTaskForCurrentUser() {
    return this.toDoTasks?.filter(
      (task) => task.user_id === SMUser.user?.ID && task.user.role === SMUser.user?.role
    );
  }

  get otherUserHasTasks() {
    return !!this.toDoTaskNotForCurrentUser?.length;
  }

  get currentUserHasTasks() {
    return !!this.toDoTaskForCurrentUser?.length;
  }

  get onlineApplicationStatus() {
    return SMCases.onlineApplicationStatus;
  }

  get exceptionsServicesForEditing() {
    return [
      SKILLED_WORKER_SERVICE_TYPE,
      SKILLED_WORKER_INSIDE_SERVICE_TYPE,
      SKILLED_WORKER_COS_SERVICE_TYPE,
      SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
    ];
  }

  get isProfileEditable() {
    const tab = SMCases.caseTabs.find((tab: API_TCaseTab) => tab.tab_type_id === this.profileId);

    if (!tab) return true;

    if (tab.role !== SMUser.user?.role) return false;

    return (
      tab.is_editable &&
      (!this.otherUserHasTasks || this.exceptionsServicesForEditing.includes(SMCases.caseType))
    );
  }

  get isCosSwInsideCase(): boolean {
    return SMCases.caseAlias === COS_SKILLED_WORKER_INSIDE_SERVICE;
  }

  get isRelevantStepsForEditing() {
    if (SMUser.isExpert) return true;

    return STEPS_FOR_EDITING.includes(SMCases.caseStepName);
  }

  get isEditEnable() {
    if (this.type === SPONSORSHIP_CANDIDATE_PROFILE) {
      return this.isRelevantStepsForEditing;
    }

    if (SMUser.isExpert) return true;

    if (SMCases.caseType === CANDIDATE_ASSESSMENT_SERVICE_TYPE) {
      // return [IN_PROGRESS_SERVICE_STATUS, REPORT_CORRECTION_SERVICE_STATUS].includes(
      //   SMCases.caseStatus
      // );
      const tab = SMCases.caseTabs.find((tab: API_TCaseTab) => tab.tab_type_id === this.profileId);

      if (!tab) return true;

      if (tab.role !== SMUser.user?.role) return false;

      return (SMCases.case as API_TCase).editing_enabled && tab.is_editable;
    }

    if (!this.isProfileEditable) {
      return false;
    }

    const relevantServiceTypeForEditing = [
      SKILLED_WORKER_SERVICE_TYPE,
      SKILLED_WORKER_COS_SERVICE_TYPE,
      SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
      SKILLED_WORKER_INSIDE_SERVICE_TYPE,
      DEPENDANT_INSIDE_SERVICE_TYPE,
    ].includes(SMCases.caseType);

    if (relevantServiceTypeForEditing) {
      const relevantSteps = [
        DATA_AND_DOCUMENTS_SUBMISSION_SERVICE_STEP,
        ADDITIONAL_INFO_FOR_EXPERT_SERVICE_STEP,
      ];

      if (this.isCosSwInsideCase) relevantSteps.push(ADDITIONAL_DATA_AND_DOCUMENTS_SERVICE_STEP);

      return relevantSteps.includes(SMCases.caseStepName);
    }

    return (
      (SMUser.isCandidate || SMUser.isEmployee || SMUser.isEmployer || SMUser.isExpert) &&
      [IN_PROGRESS_OA_STATUS, READY_TO_SUBMIT_OA_STATUS].includes(SMCases.onlineApplicationStatus)
    );
  }

  // Check for "service" edited
  // NOTE: Maybe we need to move it level upper
  async serviceEditCheck(payload: { caseId: number; stepId: number; type: string }) {
    const { caseId, stepId } = payload;

    if (!caseId || !stepId) return;

    const { data } = await this.axios.get(API_ROUTES.CASE_EDIT_CHECK(caseId));

    if (!data) return (this.notCompletedTaskDialog = true);

    return await this.confirmQuestionEditing(payload);
  }

  // NOTE: Simplify and also move level upper;
  async confirmQuestionEditing(payload: { caseId: number; stepId: number; type: string }) {
    try {
      const { caseId, stepId, type } = payload;

      if (!caseId || !stepId) return;

      const params = {};

      if (this.type === SPONSORSHIP_CANDIDATE_PROFILE) {
        Object.assign(params, {
          sponsorship_candidate_id: this.sponsorshipCandidateId,
        });
      }

      const { data } = await this.axios.get(API_ROUTES.CASE_EDIT_STEP(caseId, stepId), { params });

      if (!data) return;

      await SMQuestionnaire.openQuestionnaire(data.ID);

      if (type === QUESTIONNAIRE_STEP_TYPE_ADD) await SMQuestionnaire.addLoop();
    } catch (e) {
      console.log("confirmQuestionEditing error:", e);
    }
  }

  // Not completed tasks dialog and logic;
  notCompletedTaskDialog = false;

  closeNotCompletedTaskDialog() {
    this.notCompletedTaskDialog = false;
  }

  get homeDashboardRouteName() {
    if (SMUser.isEmployer) return EMPLOYER_DASHBOARD_ROUTE;

    if (SMUser.isCandidate) return CANDIDATE_DASHBOARD_ROUTE;

    if (SMUser.isExpert) return EXPERT_DASHBOARD_ROUTE;

    return CANDIDATE_DASHBOARD_ROUTE;
  }

  viewNotCompletedTasks() {
    if (!this.homeDashboardRouteName) return;

    this.$router.push({ name: this.homeDashboardRouteName });
  }

  // Generate notification label
  get routeName() {
    return this.$route.query.name?.toString() || "";
  }

  showNotificationForCosPlusSw() {
    if (SMUser.isEmployer) {
      if ([PERSONAL_DETAILS_SERVICE_TAB, CANDIDATE_DETAILS_SERVICE_TAB].includes(this.routeName)) {
        return this.$t("text.edit_disable.employer");
      }
    }

    if (SMUser.isCandidate || SMUser.isEmployee) {
      if ([COMPANY_DETAILS_SERVICE_TAB, EMPLOYMENT_SERVICE_TAB].includes(this.routeName)) {
        return this.$t("text.edit_disable.candidate");
      }
    }

    return this.$t("text.edit_disable.employer_candidate_cos");
  }

  get employeeDependantCaseNotificationLabel(): string {
    if (!SMUser.isEmployee) return "";

    if (SMCases.caseType !== DEPENDANT_INSIDE_SERVICE_TYPE && !SMCases.isExtension)
      return this.$t("text.edit_disable.employee").toString();

    if (SMCases.caseStepName === VISA_DECISION_SERVICE_STEP)
      return this.$t("text.edit_disable.employeeCompleted").toString();

    if (
      [
        EXPERT_REVIEW_SUBMISSION_SERVICE_STEP,
        PAYMENT_SERVICE_STEP,
        APPLICATION_SUBMISSION_SERVICE_STEP,
      ].includes(SMCases.caseStepName)
    )
      return this.$t("text.edit_disable.employeePending").toString();

    return "";
  }

  get editNotificationLabel() {
    if (this.type === SPONSORSHIP_CANDIDATE_PROFILE) {
      return [EXPERT_REVIEW_SUBMISSION_SERVICE_STEP].includes(SMCases.caseStepName)
        ? this.$tc("text.edit_disable.sponsorship_candidate.expertStep")
        : this.$tc("text.edit_disable.sponsorship_candidate.furtherStep");
    }

    if (this.employeeDependantCaseNotificationLabel)
      return this.employeeDependantCaseNotificationLabel;

    if (
      [
        SKILLED_WORKER_SERVICE_TYPE,
        SKILLED_WORKER_COS_SERVICE_TYPE,
        SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
        SKILLED_WORKER_INSIDE_SERVICE_TYPE,
        DEPENDANT_INSIDE_SERVICE_TYPE,
      ].includes(SMCases.caseType)
    ) {
      return this.showNotificationForCosPlusSw();
    }

    if (this.currentUserHasTasks) {
      if (
        (SMUser.isCandidate || SMUser.isEmployee) &&
        [PERSONAL_DETAILS_SERVICE_TAB, CANDIDATE_DETAILS_SERVICE_TAB].includes(this.routeName)
      ) {
        return this.$t("text.edit_disable.other_user_has_task");
      }

      if (SMUser.isEmployer && this.routeName === COMPANY_DETAILS_SERVICE_TAB) {
        return this.$t("text.edit_disable.other_user_has_task");
      }
    }

    if (SMCases.onlineApplicationStatus === ON_REVIEW_OA_STATUS) return this.$t("text.on_review");

    if (
      [APPROVED_BY_EXPERT_OA_STATUS, APPROVED_BY_HO_OA_STATUS, REJECTED_BY_HO_OA_STATUS].includes(
        SMCases.onlineApplicationStatus
      )
    ) {
      return this.$t("text.expert_or_ho_decision");
    }

    if (SMCases.caseType === CANDIDATE_ASSESSMENT_SERVICE_TYPE) {
      if (SMUser.isEmployer) {
        if (
          [PERSONAL_DETAILS_SERVICE_TAB, CANDIDATE_DETAILS_SERVICE_TAB].includes(this.routeName)
        ) {
          return this.$t("text.edit_disable.employer");
        }
      }

      if (SMUser.isCandidate || SMUser.isEmployee) {
        if ([COMPANY_DETAILS_SERVICE_TAB, EMPLOYMENT_SERVICE_TAB].includes(this.routeName)) {
          return this.$t("text.edit_disable.candidate");
        }
      }

      return this.$t("text.edit_disable.employer_candidate_assessment");
    }

    return this.$t("text.edit_disable.default");
  }
}
